import React, {useEffect, useState} from 'react';
import {
    cancelPaymentByAdmin,
    createPaymentByAdmin,
    fetchAllUnpaidFees,
    fetchAllUnpaidReservations, markPaymentAsPaid,
    markReservationAsPaid,
    togglePayingInProgress,
    updateReservationByAdmin
} from "../../api/api";
import {formatDate, formatTime, getStatus} from "../../utils";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './AdminPayments.module.css';
import CustomModal from "../../UI/Modal/CustomModal";
import Spinner from "../Spinner/Spinner";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

const AdminPayments = () => {

    const [unpaidReservations, setUnpaidReservations] = useState([]);
    const [unpaidFees, setUnpaidFees] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [pendingReservationId, setPendingReservationId] = useState(null);
    const [selectedReservationId, setSelectedReservationId] = useState(null);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [startDateFilter, setStartDateFilter] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), 3, 1);
    });
    const [endDateFilter, setEndDateFilter] = useState(null);
    const [nameFilter, setNameFilter] = useState('');
    const [numberOfReservationFilter, setNumberOfReservationFilter] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editingReservation, setEditingReservation] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentReservationMark, setPaymentReservationMark] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [feeIdFilter, setFeeIdFilter] = useState('');
    const [feeNameFilter, setFeeNameFilter] = useState('');
    const [feeDateFilter, setFeeDateFilter] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), 3, 1);
    });
    const [newPayment, setNewPayment] = useState({ email: '', title: '', status: 'REGISTERED', amount: '' });
    const [newPaymentError, setNewPaymentError] = useState('');
    const [newPaymentSuccess, setNewPaymentSuccess] = useState('');


    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const reservationsResponse = await fetchAllUnpaidReservations();
                const sortedReservations = reservationsResponse.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
                setUnpaidReservations(sortedReservations);
            } catch (error) {
                console.error("Failed to fetch reservations:", error);
                setError("Błąd pobrania unpaid reservations");
                setIsLoading(false);
                // Optionally set an error state here
            }

            try {
                const paymentsResponse = await fetchAllUnpaidFees();
                setUnpaidFees(paymentsResponse);
                setIsLoading(false);
            } catch (error) {
                console.error("Failed to fetch unpaid fees:", error);
                setError("Błąd pobrania unpaid fees");
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);


    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setEditingReservation(prevState => ({...prevState, [name]: value}));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const updateReservationDTO = {
            id: editingReservation.id,
            status: editingReservation.status,
            cost: parseFloat(editingReservation.cost)
        };

        try {
            const response = await updateReservationByAdmin(updateReservationDTO);
            if (response) {
                setIsEditing(false);
                const updatedReservations = await fetchAllUnpaidReservations();
                setUnpaidReservations(updatedReservations);
            } else {
                console.error("Update was not successful", response);
            }
        } catch (error) {
            console.error("Failed to update reservation:", error);
        }
    };


    const handlePaymentConfirmation = async () => {

        if (paymentReservationMark === "reservation") {
            const result = await markReservationAsPaid(selectedReservationId);
            if (result) {
                setUnpaidReservations(await fetchAllUnpaidReservations());
            } else {
                console.error("Error while confirming payment for reservation.");
            }
        } else if (paymentReservationMark === "payment") {
            const result = await markPaymentAsPaid(selectedPaymentId);
            if (result) {
                setUnpaidFees(await fetchAllUnpaidFees());
            } else {
                console.error("Error while confirming payment for reservation.");
            }
        }
        closeModal();
    };

    const openModal = (reservationId) => {
        setPendingReservationId(reservationId);
        setSelectedReservationId(reservationId);
        setPaymentReservationMark("reservation");
        setShowModal(true);
    };

    const openModalForPayment = (paymentId) => {
        setShowModal(true);
        setSelectedPaymentId(paymentId);
        setPaymentReservationMark("payment");
    };

    const handleCancelPayment = async (paymentId) => {
        const result = await cancelPaymentByAdmin(paymentId);
        if (result.success) {
            setUnpaidFees(await fetchAllUnpaidFees());
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setPendingReservationId(null);
        setSelectedReservationId(null);
        setSelectedPaymentId(null);
        setPaymentReservationMark('');
    };

    const handlePayingToggle = async (id) => {
        const result = await togglePayingInProgress(id);
        if (result) {
            setUnpaidReservations(
                unpaidReservations.map((res) =>
                    res.id === id ? {...res, payingInProgress: !res.payingInProgress} : res
                )
            );
        } else {
            console.error("Error while toggling payment in progress.");
        }
    };

    // Function to filter reservations based on the date range and name
    const filteredReservations = unpaidReservations
        .filter(reservation => {
            const startTime = new Date(reservation.startTime);
            const withinStartDate = startDateFilter ? startTime >= startDateFilter : true;
            const endDateAdjusted = endDateFilter ? new Date(endDateFilter.getTime() + (24 * 60 * 60 * 1000) - 1) : null;
            const withinEndDate = endDateAdjusted ? startTime <= endDateAdjusted : true;
            const nameMatch = `${reservation.firstName} ${reservation.lastName}`.toLowerCase().includes(nameFilter.toLowerCase());
            const numberMatch = numberOfReservationFilter ? reservation.id === parseInt(numberOfReservationFilter, 10) : true;
            const statusMatch = statusFilter ? reservation.status === statusFilter : true;
            return withinStartDate && withinEndDate && nameMatch && numberMatch && statusMatch;
        });

    const totalUnpaid = filteredReservations
        .reduce((sum, reservation) => {
            if (reservation.payingInProgress) {
                return sum;
            }
            return sum + reservation.cost;
        }, 0);

    const handleEditReservation = (reservationId) => {
        const reservationToEdit = unpaidReservations.find(reservatons => reservatons.id === reservationId);
        if (reservationToEdit) {
            setEditingReservation({...reservationToEdit});
            setIsEditing(true)
        }
    };

    if (isLoading) return <Spinner size={"large"}/>;
    if (error) return <ErrorMessage error={error}/>;

    const filteredFees = unpaidFees.filter((fee) => {
        const idMatch = feeIdFilter === '' || fee.id.toString().includes(feeIdFilter);
        const nameMatch = `${fee.firstName} ${fee.lastName}`.toLowerCase().includes(feeNameFilter.toLowerCase());
        const dateMatch = !feeDateFilter || new Date(fee.updatedAt) >= feeDateFilter;
        return idMatch && nameMatch && dateMatch;
    });

    const totalUnpaidFees = filteredFees.reduce((sum, fee)=>{
        return sum + fee.amount;
    },0);

    const handleNewPaymentSubmit = async (e) => {
        e.preventDefault();
        setNewPaymentError('');
        setNewPaymentSuccess('');
        const result = await createPaymentByAdmin({
            ...newPayment,
            amount: parseFloat(newPayment.amount)
        });
        if (result.success) {
            setNewPaymentSuccess('Płatność dodana pomyślnie');
            setNewPayment({ email: '', title: '', status: 'REGISTERED', amount: '' });
            const fees = await fetchAllUnpaidFees();
            setUnpaidFees(fees);
        } else {
            setNewPaymentError(typeof result.data === 'string' ? result.data : 'Błąd dodawania płatności');
        }
    };

    return (
        <div className={styles.AdminPayments}>
            <div className={styles.newPaymentForm}>
                <h3>Dodaj płatność</h3>
                <form onSubmit={handleNewPaymentSubmit}>
                    <div className={styles.newPaymentFields}>
                        <input
                            className={styles.newPaymentInput}
                            type="email"
                            placeholder="Email użytkownika"
                            value={newPayment.email}
                            onChange={e => setNewPayment(p => ({ ...p, email: e.target.value }))}
                            required
                        />
                        <select
                            className={styles.newPaymentInput}
                            value={newPayment.status}
                            onChange={e => setNewPayment(p => ({ ...p, status: e.target.value }))}
                        >
                            <option value="REGISTERED">Zarejestrowana</option>
                            <option value="PAID">Zapłacona</option>
                            <option value="PENDING">Oczekująca</option>
                            <option value="REJECTED">Anulowana</option>
                        </select>
                        <input
                            className={styles.newPaymentInput}
                            type="text"
                            placeholder="Opis płatności"
                            value={newPayment.title}
                            onChange={e => setNewPayment(p => ({ ...p, title: e.target.value }))}
                            required
                        />
                        <input
                            className={styles.newPaymentInput}
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Kwota (PLN)"
                            value={newPayment.amount}
                            onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                            required
                        />
                        <button className={styles.button} type="submit">Dodaj</button>
                    </div>
                    {newPaymentError && <div className={styles.errorMsg}>{newPaymentError}</div>}
                    {newPaymentSuccess && <div className={styles.successMsg}>{newPaymentSuccess}</div>}
                </form>
            </div>
            <CustomModal
                isOpen={showModal}
                onRequestClose={closeModal}
                message={paymentReservationMark === "reservation" ? `PAID dla rezerwacji nr ${selectedReservationId}?`: `PAID dla płatności nr ${selectedPaymentId}?`}
                confirmLabel="Tak"
                confirmButtonColor="green"
                onConfirm={paymentReservationMark === "reservation" ? () => handlePaymentConfirmation(pendingReservationId): () => handlePaymentConfirmation()}
                cancelLabel="Nie"
                cancelButtonColor="red"
                onCancel={closeModal}
            />
            {unpaidReservations.length === 0 && unpaidFees.length === 0 && (
                <p>Brak zaległych opłat.</p>
            )
            }
            <div className={styles.tableContainer}>
                {unpaidReservations.length > 0 && (
                    <>

                        <div className={styles.filterContainer}>
                            <div>
                                <input
                                    className={styles.datepicker}
                                    type="number"
                                    placeholder="Nr"
                                    value={numberOfReservationFilter || ''}
                                    onChange={(e) => setNumberOfReservationFilter(e.target.value ? parseInt(e.target.value, 10) : null)}

                                />
                                <DatePicker
                                    className={styles.datepicker}
                                    selected={startDateFilter}
                                    onChange={(date) => setStartDateFilter(date)}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="From date"
                                />
                                <DatePicker
                                    className={styles.datepicker}
                                    selected={endDateFilter}
                                    onChange={(date) => setEndDateFilter(date)}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="To date"
                                />
                                <input
                                    className={styles.datepicker}
                                    type="text"
                                    placeholder="Imię i Nazwisko"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                />
                                <select
                                    name="statusFilter"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className={styles.datepicker}
                                >
                                    <option value="">Select status</option>
                                    <option value="NMEMBER">NME</option>
                                    <option value="LESSONS">LES</option>
                                    <option value="REGISTERED">REG</option>
                                </select>
                            </div>
                        </div>
                        {isEditing &&
                            <form onSubmit={handleSubmit}>
                                <div className={styles.filterContainer}>
                                    <div>
                                        <p className={styles.datepicker}>{editingReservation.id}</p>
                                    </div>
                                    <div>
                                        <p className={styles.datepicker}>{`${editingReservation.firstName} ${editingReservation.lastName}`}</p>
                                    </div>
                                    <div>
                                        <select
                                            name="status"
                                            value={editingReservation?.status || ''}
                                            onChange={handleInputChange}
                                            className={styles.datepicker}
                                        >
                                            <option value="MEMBER">MEM</option>
                                            <option value="NMEMBER">NME</option>
                                            <option value="CANCELED">CAN</option>
                                            <option value="PAYED">PAY</option>
                                            <option value="LESSONS">LES</option>
                                            <option value="REGISTERED">REG</option>
                                            <option value="PERIODIC">PER</option>
                                        </select>
                                    </div>
                                    <div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="cost"
                                            value={editingReservation.cost}
                                            placeholder="Kwota"
                                            onChange={handleInputChange} className={styles.cost}/>
                                    </div>
                                </div>
                                <div className={styles.buttonContainer}>
                                    <button className={styles.button} type="submit">Zapisz</button>
                                    <button className={styles.button} type="button"
                                            onClick={() => setIsEditing(false)}>Anuluj
                                    </button>
                                </div>
                            </form>
                        }
                        {
                            <div className={styles.tableContainer}>
                                <table className={styles.reservationsTable}>
                                    <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Nr</th>
                                        <th>Kort</th>
                                        <th>Czas</th>
                                        <th className={styles.totalUnpaid}>{totalUnpaid.toFixed(2)}</th>
                                        <th>Stat</th>
                                        <th>Imię i nazwisko</th>
                                        <th>Telefon</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredReservations
                                        .map(({
                                                  id,
                                                  startTime,
                                                  courtId,
                                                  endTime,
                                                  cost,
                                                  status,
                                                  firstName,
                                                  lastName,
                                                  phone
                                              }) => (
                                            <tr key={id}>
                                                <td>{formatDate(startTime, true)}</td>
                                                <td>{id}</td>
                                                <td>{courtId}</td>
                                                <td>{`${formatTime(startTime)}-${formatTime(endTime)}`}</td>
                                                <td>{cost.toFixed(2)}</td>
                                                <td>{getStatus(status)}</td>
                                                <td>{`${firstName} ${lastName}`}</td>
                                                <td>{phone}</td>
                                                <td>
                                                    <button className={styles.paidButton}
                                                            onClick={() => openModal(id)}>Zapłacone
                                                    </button>

                                                </td>
                                                <td>
                                                    <button className={styles.paidButton}
                                                            onClick={() => handleEditReservation(id)}>Edytuj
                                                    </button>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        }
                    </>
                )}

                <div className={styles.filterContainer}>
                    <input
                        className={styles.datepicker}
                        type="number"
                        placeholder="Nr"
                        value={feeIdFilter}
                        onChange={(e) => setFeeIdFilter(e.target.value)}
                    />
                    <DatePicker
                        className={styles.datepicker}
                        selected={feeDateFilter}
                        onChange={(date) => setFeeDateFilter(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="Data od"
                    />
                    <input
                        className={styles.datepicker}
                        type="text"
                        placeholder="Imię i nazwisko"
                        value={feeNameFilter}
                        onChange={(e) => setFeeNameFilter(e.target.value)}
                    />
                </div>

                {filteredFees.length > 0 && (
                    <div className={styles.tableContainer}>
                        <table className={styles.reservationsTable}>
                            <thead>
                            <tr>
                                <th>Nr</th>
                                <th>Imię i Nazwisko</th>
                                <th>Rodzaj opłaty</th>
                                <th>Stat</th>
                                <th className={styles.totalUnpaid}>{totalUnpaidFees.toFixed(2)}</th>
                                <th>Płatność</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredFees.length > 0 && (
                                filteredFees.map(({
                                                    id,
                                                    firstName,
                                                    lastName,
                                                    title,
                                                    status,
                                                    amount
                                                }) => (
                                    <tr key={id}>
                                        <td>{id}</td>
                                        <td>{`${firstName} ${lastName}`}</td>
                                        <td>{title}</td>
                                        <td>{getStatus(status)}</td>
                                        <td>{amount}</td>
                                        <td>
                                            <button
                                                className={styles.button}
                                                onClick={() => openModalForPayment(id)}
                                            >
                                                Zapłacone
                                            </button>
                                            <button
                                                className={styles.cancelButton}
                                                onClick={() => handleCancelPayment(id)}
                                            >
                                                Anuluj
                                            </button>
                                        </td>
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPayments;
